const PLACEHOLDER = { dd: "d", mm: "m", yyyy: "y" };

export function transformDateInput(
  raw: string,
  value: string,
  selection: { start: number; end: number }
) {
  const MAX_LENGTH = 8;

  let isFreezeCursor = false;
  let error = false;
  const isDeleted = raw.length < value.length;

  let cleaned = raw.replace(/\D/g, "");
  const prev = value.replace(/\D/g, "");

  // Limit max length
  if (cleaned.length > MAX_LENGTH) {
    return {
      result: raw.slice(0, 14),
      cursor: { start: 14, end: 14 },
      error,
    };
  }

  if (cleaned.length === 0) {
    return {
      result: "",
      cursor: { start: 0, end: 0 },
      error,
    };
  }

  // Handle character deletion
  if (isDeleted && cleaned === prev) {
    const idx = selection.start;
    if (idx <= 5) {
      cleaned = cleaned.slice(0, 1) + cleaned.slice(2);
    } else if (idx <= 10) {
      cleaned = cleaned.slice(0, 3) + cleaned.slice(4);
    }
  }

  const dd = cleaned.slice(0, 2);
  let mm = cleaned.slice(2, 4);
  let yyyy = cleaned.slice(4, 8);

  const ddNum = parseInt(dd, 10);
  const mmNum = parseInt(mm, 10);
  const yyyyNum = parseInt(yyyy, 10);

  // === VALIDATIONS ===

  // 1. Validate day when only day is filled
  if (dd.length === 2 && mm.length === 1 && !isValidDay(dd)) {
    mm = PLACEHOLDER.mm + PLACEHOLDER.mm;
    isFreezeCursor = true;
    error = true;
  }

  // 2. Validate full day-month pair
  if (
    mm.length === 2 &&
    yyyy.length === 1 &&
    (!isValidMonth(mm) || !isValidDayMonthPair(ddNum, mmNum))
  ) {
    yyyy = PLACEHOLDER.yyyy.repeat(4);
    isFreezeCursor = true;
    error = true;
  }

  // 3. Validate February with leap year check
  if (
    yyyy.length === 4 &&
    mmNum === 2 &&
    !isValidLeapYearWithDay(yyyyNum, ddNum)
  ) {
    error = true;
  }

  // === PADDED RESULT ===
  const padded = {
    dd: dd.padEnd(2, PLACEHOLDER.dd),
    mm: mm.padEnd(2, PLACEHOLDER.mm),
    yyyy: yyyy.padEnd(4, PLACEHOLDER.yyyy),
  };

  const result = `${padded.dd} - ${padded.mm} - ${padded.yyyy}`;

  // === CURSOR LOGIC ===
  let next = selection.start;

  // Handle cursor position based on input
  if (!isFreezeCursor) {
    const diff = cleaned.length - prev.length;

    // jump cursor to next segment
    const forward: Record<number, number> = { 2: 5, 4: 10 };
    const backward: Record<number, number> = { 1: 1, 3: 6 };

    if (!isDeleted) {
      if (diff) {
        next = forward[cleaned.length] ?? next + 1;
      }
    } else {
      next = backward[cleaned.length] ?? next - 1;
    }
  }

  return {
    result,
    cursor: { start: next, end: next },
    error,
  };
}

export function fixedDateStringCombination(raw: string) {
  let isValid = true;
  const cleaned = raw.replace(/\D/g, "");

  if (cleaned.length === 0) {
    return { fixed: "", isValid: false };
  }

  let dd = cleaned.slice(0, 2);
  let mm = cleaned.slice(2, 4);
  const yyyy = cleaned.slice(4, 8);

  if (dd.length === 2 && !isValidDay(dd)) {
    isValid = false;
    dd = `0${dd.slice(0, 1)}`;
  }

  if (mm.length === 2 && !isValidMonth(mm)) {
    isValid = false;
    mm = `0${mm.slice(0, 1)}`;
  }

  const padded = {
    dd: dd.padEnd(2, PLACEHOLDER.dd),
    mm: mm.padEnd(2, PLACEHOLDER.mm),
    yyyy: yyyy.padEnd(4, PLACEHOLDER.yyyy),
  };

  return {
    fixed: `${padded.dd} - ${padded.mm} - ${padded.yyyy}`,
    isValid,
  };
}

export function formateDateToStringTemplate(date: Date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());

  return `${dd} - ${mm} - ${yyyy}`;
}

export function parseStringTemplateToDate(str: string): Date {
  const parts = str.split(/[^0-9]/).filter(Boolean);
  const [dd, mm, yyyy] = parts.map((n) => parseInt(n, 10));
  return new Date(yyyy, mm - 1, dd);
}

// === VALIDATION HELPERS ===

const isValidDay = (day: string): boolean => {
  const d = parseInt(day, 10);
  return d >= 1 && d <= 31;
};

const isValidMonth = (month: string): boolean => {
  const m = parseInt(month, 10);
  return m >= 1 && m <= 12;
};

const isValidDayMonthPair = (day: number, month: number): boolean => {
  if (month < 1 || month > 12 || day < 1) return false;
  const maxDays = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= maxDays[month - 1];
};

const isValidLeapYearWithDay = (year: number, day: number): boolean => {
  if (year < 1 || day < 1) return false;
  const isLeap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return day <= (isLeap ? 29 : 28);
};
